/* eslint-disable react-hooks/rules-of-hooks */
import { useState } from 'react'
import { BatterySaver } from '@mui/icons-material'
import { Appliance, ApplianceConfigBattery } from './Appliance'
import { EnergyStorageDriver, PortConfig, PortType } from '../../types/ports'
import { BatteryStateWidget } from '../widgets/BatteryStateWidget'
import { getWidget } from '../widgets'
import { Box, Typography } from '@mui/material'
import { Trans } from 'react-i18next'
import { StrategySelector } from '../../components/StrategySelector'
import { BatteryOptimizerPortControl } from '../ports/BatteryOptimizerPortControl'
import { EnergyStoragePortControl } from '../ports/EnergyStoragePortControl'
import { ID, Port, Strategy } from '../../types/models'
import { PortControl } from '../ports/PortControl'
import { CustomSwitch } from '../../components/atoms/CustomSwitch'
import { hasRelayedAppliance } from './relayed-sub-appliance'
import { ModbusDialog } from '../../components/ModbusDialog'
import { Button } from '../../components/atoms/Button'

function ModbusToiminnotButton({ portId, hwId, driver, slaveId, emSlaveId }: { portId: ID, hwId: string, driver?: EnergyStorageDriver, slaveId?: number, emSlaveId?: number }) {
  const [open, setOpen] = useState(false)
  return <>
    <Button onClick={() => setOpen(true)} data-cy="button-Modbus">Modbus</Button>
    <ModbusDialog open={open} onClose={() => setOpen(false)} portId={portId} hwId={hwId} driver={driver} slaveId={slaveId} emSlaveId={emSlaveId} />
  </>
}

export class BatteryAppliance extends Appliance<{ }, ApplianceConfigBattery> {
  get Icon() {
    return BatterySaver
  }

  hasTestPage() {
    return false
  }

  hasSocControls() {
    return true
  }

  getDefaultPortType(): PortType {
    return 'ENERGY_STORAGE'
  }

  getDefaultConfig(): ApplianceConfigBattery {
    return {
      has_han: false,
      has_solar: false,
      strategy: 'BATTERY_OPTIMIZING',
    }
  }

  /**
   * The selected strategy. Appliances saved before the manual mode existed
   * have no `strategy` at all and are optimizing.
   */
  getStrategy(): Strategy {
    return (this.getConfig('strategy') as Strategy) || 'BATTERY_OPTIMIZING'
  }

  /**
   * Maintenance mode: the firmware's battery optimizer is off and the battery
   * takes direct power commands from the front page instead of following a
   * plan.
   */
  isManualControl(): boolean {
    return this.getStrategy() === 'BATTERY_MANUAL'
  }

  /**
   * Red warnings shown under the strategy selector in manual mode.
   */
  manualModeWarnings(): string[] {
    return [this.app.t('This function is meant for maintenance and testing only.')]
  }

  getDefaultWidgets(): [BatteryStateWidget] {
    return [
      getWidget({
        name: '',
        type: 'BATTERY_STATE',
        appliances: [this],
        config: {}
      }, this.control, this.app) as BatteryStateWidget,
    ]
  }

  /**
   * Find the battery control.
   */
  getBatteryControl(): EnergyStoragePortControl {
    // Hmm, this is slightly questionable. Can we quarantee that in future it will be second control?
    return this.getControl(0)
  }

  /**
   * Find the optimizer control.
   */
  getOptimizerControl(): BatteryOptimizerPortControl {
    // Hmm, this is slightly questionable. Can we quarantee that in future it will be second control?
    return this.getControl(1)
  }

  configTop(appliances: Appliance[]): React.ReactNode {
    const strategy = this.getStrategy()

    const onSelect = (selected: Strategy) => {
      this.setConfig('strategy', selected)
      // Port 0x220 `enabled` is the master switch for the firmware's battery
      // optimizer: with it off, the optimizer stops driving port 22/0 and stops
      // calculating FCR bids and swarmtune. Only this one field changes — the
      // rest of the optimizer conf (power limits, SOC limits, penalties) is
      // left as it is, so switching back restores it.
      //
      // On a reserve battery this is necessary but NOT sufficient: the
      // firmware's FCR commander runs from a 100 ms interrupt and re-stamps the
      // inverter setpoint whenever the energy-storage port is enabled,
      // regardless of the optimizer. FCRBatteryAppliance therefore also has the
      // unit's FCR port switched off server-side; see its beforeFlash().
      this.getOptimizerControl().setConfig('enabled', selected !== 'BATTERY_MANUAL')
    }

    return <Box>
      <Trans>Strategy</Trans>
      <StrategySelector
        battery
        // Maintenance mode is an installer tool. An ordinary user still sees
        // the chip once the battery is in it, so they are not stuck.
        allowManual={this.app.installerLevel || strategy === 'BATTERY_MANUAL'}
        value={strategy}
        onSelect={onSelect}
      />
      {strategy === 'BATTERY_MANUAL' &&
        <Box data-cy="box-Manual control warning" sx={{ mt: 1.5 }}>
          {this.manualModeWarnings().map((warning, idx) =>
            // The colour has to sit on the Typography: it sets text.primary of
            // its own accord and would otherwise ignore a colour inherited
            // from the Box.
            <Typography key={idx} sx={{ color: 'error.main', fontWeight: 'bold' }}>{warning}</Typography>
          )}
        </Box>
      }
    </Box>
  }

  configBottom(appliances: Appliance[]): React.ReactNode {
    const ctrl1 = this.getBatteryControl()
    const ctrl2 = this.getOptimizerControl()
    const driver = ctrl1.getConfig('driver') as EnergyStorageDriver

    const allPortsInDevice = ctrl1.deviceControl.device.ports?.map(port => port.id)

    // Solar show slider logic
    const numberOfSolarPorts = ctrl1.deviceControl.device.ports?.filter(port => port.type === 'SOLAR').length as unknown as number
    const existingSolarsInDevice = appliances.filter(appliance => appliance.getSubtype() === 'SOLAR' && allPortsInDevice?.includes(appliance.portIDs[0]))
    let isShowSolarSlider = !hasRelayedAppliance(ctrl1.deviceControl.device, appliances, 'SOLAR')
    if (existingSolarsInDevice.length === numberOfSolarPorts) isShowSolarSlider = false

    // HAN show slider logic
    const numberOfHanPorts = ctrl1.deviceControl.device.ports?.filter(port => port.type === 'EM_HAN').length as unknown as number
    const existingHansInDevice = appliances.filter(appliance => appliance.getSubtype() === 'EM_HAN' && allPortsInDevice?.includes(appliance.portIDs[0]))
    let isShowHanSlider = !hasRelayedAppliance(ctrl1.deviceControl.device, appliances, 'EM_HAN')
    if (existingHansInDevice.length === numberOfHanPorts) isShowHanSlider = false

    const handleToggleHanOption = () => {
      if (this.getConfig('has_han')) {
        this.setConfig('has_han', false)
      } else {
        this.setConfig('has_han', true)
      }
    }

    const handleToggleSolarOption = () => {
      if (this?.getConfig('has_solar')) {
        this.setConfig('has_solar', false)
      } else {
        this?.setConfig('has_solar', true)
      }
    }

    const portId = ctrl1.port.id as ID
    const hwId = ctrl1.deviceControl.device.hw_id
    const params = (ctrl1.getConfig('params') || {}) as { slave_id?: number, em_slave_id?: number }

    // Everything ctrl2 renders (charge/discharge power limits, minimum SOC,
    // algorithm fine-tuning) configures the battery optimizer, which manual
    // mode switches off. Hide it rather than show settings that do nothing.
    const manual = this.isManualControl()

    return <>
      <Box>{ctrl1.configBottom()}{!manual && ctrl2.configBottom(this.hasSocControls(), this.selfUseMaxPowerW(), this.reserveTuning())}</Box>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
        {this.extraSettingsButtons()}
        <ModbusToiminnotButton portId={portId} hwId={hwId} driver={driver} slaveId={params.slave_id} emSlaveId={params.em_slave_id} />
      </Box>
      <Box sx={{ mt: 4 }}>
        {isShowHanSlider && <Box>
          <Typography>{this.app.t('Is there energy meter connected to the inverter? Allow relaying the data to Enion here.')}</Typography>
          {driver === 'SPP_ENERGYSTORAGE_DRIVER_KOSTAL_PLENTICORE' && <Typography>{this.app.t('Note! Inverter firmware must be updated to newest version for energy meter relaying to work. Contact Kostal support for more information.')}</Typography>}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box data-cy='box-Add HAN relay' sx={{ mt: 2 }}>
              <CustomSwitch onClick={handleToggleHanOption} data-cy='switch-HAN with Battery' sx={{ mr: '1rem' }} checked={this.getConfig('has_han')}/>
              <Trans>ADD ENERGY METER DATA RELAYING</Trans>
            </Box>
          </Box>
        </Box>}
        <Box sx={{ mt: 4 }}>
        {isShowSolarSlider && <>
            <Typography>{this.app.t('Solar panels connected to inverter? Allow relaying the solar production data to Enion from here.')}</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box data-cy='box-Add PV relay' sx={{ mt: 2 }}>
                <CustomSwitch onClick={handleToggleSolarOption} data-cy='switch-Solar with Battery' sx={{ mr: '1rem' }} checked={this.getConfig('has_solar')}/>
                <Trans>ADD PV DATA RELAYING</Trans>
              </Box>
            </Box>
          </>}
        </Box>
      </Box>
    </>
  }

  /**
   * Upper end of the self-use power limit sliders: the inverter's nominal
   * power when it is known. `null` keeps the plain text fields.
   */
  selfUseMaxPowerW(): number | null {
    return null
  }

  /**
   * Settings shown directly above the algorithm fine-tuning. Reserve
   * batteries add their reserve participation tuning here.
   */
  reserveTuning(): React.ReactNode {
    return null
  }

  /**
   * Action buttons appended to the right of the Modbus button row.
   * Subclasses override this to add their own buttons (e.g. FCR calibration).
   */
  extraSettingsButtons(): React.ReactNode {
    return null
  }

  filterSuitablePorts(ports: PortControl<Port, PortConfig>[]): PortControl<Port, PortConfig>[] {
    return this.autoAllocate(ports)
  }

}
